import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Trash } from "@phosphor-icons/react";

/**
 * Reusable delete confirmation dialog — requires typing "DELETE" to proceed.
 * Props: open, onClose, onConfirm, title, description (optional JSX)
 */
export function DeleteConfirmDialog({ open, onClose, onConfirm, title = "Confirm deletion", description, busy }) {
  const [typed, setTyped] = useState("");
  useEffect(() => { if (!open) setTyped(""); }, [open]);
  const confirmed = typed.trim().toUpperCase() === "DELETE";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        {description && <div className="text-sm text-[#374151]">{description}</div>}
        {/* Callers signal "this cannot be deleted" by passing no onConfirm, and
            then `description` is the explanation. Previously the dialog still
            rendered the type-DELETE box and a live-looking red Delete button
            that silently did nothing when clicked — the confirmation ritual with
            its wiring cut. When there is nothing to confirm, show only the
            explanation and a way out. */}
        {onConfirm ? (
          <>
            <label className="mt-1 block text-xs font-semibold text-[#6b7280]">
              Type <span className="font-bold text-[#F01919]">DELETE</span> to confirm
              <input
                autoFocus
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                className="mt-1 block h-9 w-full rounded-md border border-[#C2C9D1] px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F01919]/30"
                placeholder="Type the word shown above"
                data-testid="delete-confirm-input"
              />
            </label>
            <DialogFooter className="mt-2 gap-2">
              <button onClick={onClose} className="flex-1 rounded-md border border-[#C2C9D1] px-3 py-1.5 text-xs font-semibold text-[#374151] hover:bg-[#F3F4F6]" data-testid="delete-confirm-cancel">Cancel</button>
              <button
                onClick={onConfirm}
                disabled={!confirmed || busy}
                className="inline-flex flex-1 items-center justify-center gap-1 rounded-md bg-[#F01919] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#c91010] disabled:opacity-40"
                data-testid="delete-confirm-ok"
              >
                <Trash size={14} weight="bold" /> {busy ? "Deleting…" : "Delete"}
              </button>
            </DialogFooter>
          </>
        ) : (
          <DialogFooter className="mt-2">
            <button onClick={onClose} className="w-full rounded-md border border-[#C2C9D1] px-3 py-1.5 text-xs font-semibold text-[#374151] hover:bg-[#F3F4F6]" data-testid="delete-confirm-cancel">Close</button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
